import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  MdArrowBack, 
  MdEmail, 
  MdPhone, 
  MdLocationOn,
  MdPerson,
  MdEdit,
  MdDelete,
  MdVisibility,
  MdCalendarToday,
  MdAttachMoney,
  MdPayment,
  MdRepeat,
  MdCheckCircle,
  MdCancel
} from 'react-icons/md';
import { clientesAPI, recibosAPI, recibosRecurrentesAPI, configuracionAPI } from '../services/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const ClientProfileContainer = styled.div`
  width: 100%;
  max-width: 100%;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.lg};
`;

const BackButton = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.colors.backgroundLight};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  color: ${props => props.theme.colors.textPrimary};
  transition: ${props => props.theme.transitions.fast};
  cursor: pointer;

  svg {
    font-size: 1.5rem;
  }

  &:hover {
    background-color: ${props => props.theme.colors.background};
    border-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primary};
  }
`;

const Title = styled.h1`
  font-size: ${props => props.theme.fontSizes.xxxl};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
`;

const Button = styled.button`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  background-color: ${props => props.theme.colors.backgroundLight};
  color: ${props => props.theme.colors.textPrimary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  transition: ${props => props.theme.transitions.fast};
  cursor: pointer;

  svg {
    font-size: 1.25rem;
  }

  &:hover {
    background-color: ${props => props.theme.colors.background};
    border-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primary};
  }
`;

const PrimaryButton = styled(Button)`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  border-color: ${props => props.theme.colors.primary};

  &:hover {
    background-color: ${props => props.theme.colors.primaryDark};
    color: white;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.xl};
  margin-bottom: ${props => props.theme.spacing.xl};

  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${props => props.theme.colors.backgroundLight};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  box-shadow: ${props => props.theme.shadows.sm};
  border: 1px solid ${props => props.theme.colors.border};
`;

const CardTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes.xl};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 ${props => props.theme.spacing.lg} 0;
  font-weight: ${props => props.theme.fontWeights.semibold};
`;

const ClientInfoSection = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const Avatar = styled.div`
  width: 100px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  border-radius: ${props => props.theme.borderRadius.round};
  overflow: hidden;
  flex-shrink: 0;
  border: 3px solid ${props => props.theme.colors.border};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ClientDetails = styled.div`
  flex: 1;
`;

const ClientName = styled.h3`
  font-size: ${props => props.theme.fontSizes.xxl};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 ${props => props.theme.spacing.xs} 0;
  font-weight: ${props => props.theme.fontWeights.bold};
`;

const ClientEmail = styled.p`
  font-size: ${props => props.theme.fontSizes.md};
  color: ${props => props.theme.colors.textSecondary};
  margin: 0 0 ${props => props.theme.spacing.lg} 0;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};

  svg {
    color: ${props => props.theme.colors.primary};
    font-size: 1.125rem;
  }
`;

const FinancialSummary = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.lg};
`;

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const SummaryLabel = styled.span`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  font-weight: ${props => props.theme.fontWeights.medium};
`;

const SummaryValue = styled.span`
  font-size: ${props => props.theme.fontSizes.xxl};
  color: ${props => props.theme.colors.textPrimary};
  font-weight: ${props => props.theme.fontWeights.bold};
`;

const SummarySubtext = styled.span`
  font-size: ${props => props.theme.fontSizes.xs};
  color: ${props => props.theme.colors.textSecondary};
`;

const RecurrentCard = styled.div`
  background: linear-gradient(to bottom right, ${props => props.theme.colors.primary}, ${props => props.theme.colors.primaryDark});
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  color: white;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
  }
`;

const RecurrentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.lg};
  position: relative;
  z-index: 1;
  
  svg {
    font-size: 2rem;
    opacity: 0.9;
  }
  
  h3 {
    margin: 10px 0;
    font-size: ${props => props.theme.fontSizes.xl};
  }
`;

const RecurrentInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${props => props.theme.spacing.lg};
  position: relative;
  z-index: 1;
`;

const RecurrentInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const RecurrentLabel = styled.span`
  font-size: ${props => props.theme.fontSizes.xs};
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const RecurrentValue = styled.span`
  font-size: ${props => props.theme.fontSizes.xl};
  font-weight: ${props => props.theme.fontWeights.bold};
`;

const RecurrentBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.md};
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  backdrop-filter: blur(10px);
  
  svg {
    font-size: 1rem;
  }
`;

const NoRecurrentCard = styled.div`
  background: ${props => props.theme.colors.background};
  border: 2px dashed ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};
  
  svg {
    font-size: 3rem;
    opacity: 0.3;
    margin-bottom: ${props => props.theme.spacing.md};
  }
  
  p {
    margin: 0;
    font-size: ${props => props.theme.fontSizes.sm};
  }
`;

const DueDateBadge = styled.span`
  display: inline-block;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: ${props => props.theme.fontWeights.semibold};
  background-color: ${props => props.theme.colors.danger}20;
  color: ${props => props.theme.colors.danger};
  margin-top: ${props => props.theme.spacing.xs};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background-color: ${props => props.theme.colors.background};
`;

const Th = styled.th`
  padding: ${props => props.theme.spacing.md};
  text-align: left;
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.semibold};
  color: ${props => props.theme.colors.textSecondary};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const Td = styled.td`
  padding: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textPrimary};
  border-bottom: 1px solid ${props => props.theme.colors.borderLight};
`;

const StatusBadge = styled.span`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: ${props => props.theme.fontWeights.medium};
  background-color: ${props => {
    if (props.status === 'Pagado') return props.theme.colors.success + '20';
    if (props.status === 'Vencido') return props.theme.colors.danger + '20';
    return props.theme.colors.warning + '20';
  }};
  color: ${props => {
    if (props.status === 'Pagado') return props.theme.colors.success;
    if (props.status === 'Vencido') return props.theme.colors.danger;
    return props.theme.colors.warning;
  }};
`;

const ActionButton = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.primary};
  color: white;
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  transition: ${props => props.theme.transitions.fast};
  border: none;
  cursor: pointer;

  svg {
    font-size: 1rem;
  }

  &:hover {
    background-color: ${props => props.theme.colors.primaryDark};
  }
`;

const RemindersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const ReminderItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing.md};
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};
`;

const ReminderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  flex: 1;
`;

const ReminderIcon = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.colors.primary}15;
  border-radius: ${props => props.theme.borderRadius.md};
  color: ${props => props.theme.colors.primary};

  svg {
    font-size: 1.25rem;
  }
`;

const ReminderText = styled.span`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textPrimary};
  font-weight: ${props => props.theme.fontWeights.medium};
`;

const ReminderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
`;

const ReminderStatus = styled.span`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: ${props => props.theme.fontWeights.semibold};
  background-color: ${props => props.active ? props.theme.colors.success + '20' : props.theme.colors.textLight + '20'};
  color: ${props => props.active ? props.theme.colors.success : props.theme.colors.textSecondary};
`;

const IconButton = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.sm};
  color: ${props => props.theme.colors.textSecondary};
  cursor: pointer;
  transition: ${props => props.theme.transitions.fast};

  svg {
    font-size: 1rem;
  }

  &:hover {
    background-color: ${props => props.theme.colors.background};
    border-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primary};
  }
`;

const AddReminderButton = styled.button`
  width: 100%;
  padding: ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.backgroundLight};
  color: ${props => props.theme.colors.primary};
  border: 2px dashed ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.semibold};
  cursor: pointer;
  transition: ${props => props.theme.transitions.fast};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.sm};

  &:hover {
    background-color: ${props => props.theme.colors.background};
    border-color: ${props => props.theme.colors.primary};
  }
`;

const FullWidthCard = styled(Card)`
  grid-column: 1 / -1;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const ClientProfile = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  const [client, setClient] = useState(null);
  const [recibos, setRecibos] = useState([]);
  const [recibosRecurrentes, setRecibosRecurrentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    loadClientData();
    loadConfig();
  }, [id]);

  const loadConfig = async () => {
    try {
      const response = await configuracionAPI.getConfiguracion();
      setConfig(response.data.data);
    } catch (error) {
      console.error('Error al cargar configuración:', error);
    }
  };

  const loadClientData = async () => {
    try {
      setLoading(true);
      
      // Cargar datos del cliente
      const clienteResponse = await clientesAPI.getById(id);
      setClient(clienteResponse.data.data);
      
      // Cargar recibos del cliente
      const recibosResponse = await recibosAPI.getByCliente(id);
      setRecibos(recibosResponse.data.data || []);
      
      // Cargar recibos recurrentes del cliente
      try {
        const recurrentesResponse = await recibosRecurrentesAPI.getByCliente(id);
        setRecibosRecurrentes(recurrentesResponse.data.data || []);
      } catch (err) {
        console.log('No hay recibos recurrentes o error al cargar:', err);
        setRecibosRecurrentes([]);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error al cargar datos del cliente:', err);
      setError(err.message || 'Error al cargar datos del cliente');
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePDF = () => {
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      let yPosition = 12;
      
      // Configuración de la empresa
      const empresa = config?.nombre_empresa || 'Mi Empresa';
      const direccion = config?.direccion || '';
      const telefono = config?.telefono || '';
      const email = config?.email_contacto || '';
      const logoUrl = config?.logo_url || '';

      // Encabezado con logo y nombre de empresa
      const headerStartY = yPosition;
      
      // Agregar logo si existe (lado izquierdo)
      if (logoUrl && logoUrl.startsWith('data:image')) {
        try {
          const logoSize = 20;
          const logoX = 20;
          doc.addImage(logoUrl, 'PNG', logoX, yPosition, logoSize, logoSize);
        } catch (error) {
          console.error('Error al agregar logo:', error);
        }
      }

      // Información de la empresa (lado derecho del logo o centrado si no hay logo)
      const textX = logoUrl && logoUrl.startsWith('data:image') ? 45 : pageWidth / 2;
      const textAlign = logoUrl && logoUrl.startsWith('data:image') ? 'left' : 'center';
      
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(empresa, textX, yPosition + 5, { align: textAlign });
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      let infoY = yPosition + 10;
      
      if (direccion) {
        doc.text(direccion, textX, infoY, { align: textAlign });
        infoY += 4;
      }
      if (telefono || email) {
        const contacto = [telefono, email].filter(Boolean).join(' | ');
        doc.text(contacto, textX, infoY, { align: textAlign });
        infoY += 4;
      }
      
      yPosition = Math.max(headerStartY + 20, infoY);

      // Línea separadora
      yPosition += 3;
      doc.setDrawColor(200, 200, 200);
      doc.line(15, yPosition, pageWidth - 15, yPosition);
      yPosition += 8;
      
      // Título del documento
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(91, 155, 213);
      doc.text('ESTADO DE CUENTA', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;
      doc.setTextColor(0, 0, 0);
      
      // Información del cliente en contenedor gris
      const containerMargin = 15;
      const containerWidth = pageWidth - (containerMargin * 2);
      
      doc.setFillColor(245, 245, 245);
      doc.rect(containerMargin, yPosition - 3, containerWidth, 32, 'F');
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('CLIENTE', containerMargin + 5, yPosition);
      yPosition += 6;
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(client?.nombre || 'N/A', containerMargin + 5, yPosition);
      yPosition += 5;
      
      doc.setFontSize(9);
      let clienteInfo = '';
      if (client?.rfc) {
        clienteInfo = `RFC: ${client.rfc}`;
      }
      if (client?.telefono) {
        clienteInfo += (clienteInfo ? ' • ' : '') + `Tel: ${client.telefono}`;
      }
      if (client?.email) {
        clienteInfo += (clienteInfo ? ' • ' : '') + client.email;
      }
      
      if (clienteInfo) {
        const maxWidth = containerWidth - 10;
        const infoLines = doc.splitTextToSize(clienteInfo, maxWidth);
        doc.text(infoLines, containerMargin + 5, yPosition);
        yPosition += (infoLines.length * 4);
      }
      yPosition += 8;
      
      // Fecha de generación
      doc.setFontSize(9);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(100, 100, 100);
      const fechaActual = new Date().toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      doc.text(`Fecha de generación: ${fechaActual}`, containerMargin + 5, yPosition);
      yPosition += 8;
      doc.setTextColor(0, 0, 0);
      
      // Calcular resumen
      const totalRecibos = recibos.length;
      const totalMonto = recibos.reduce((sum, r) => sum + parseFloat(r.monto_total || 0), 0);
      const totalPagado = recibos.reduce((sum, r) => sum + (parseFloat(r.monto_total || 0) - parseFloat(r.saldo_pendiente || 0)), 0);
      const totalPendiente = recibos.reduce((sum, r) => sum + parseFloat(r.saldo_pendiente || 0), 0);
      
      // Encabezado de resumen
      doc.setFillColor(240, 248, 255); // Azul muy claro
      doc.rect(containerMargin, yPosition - 2, containerWidth, 8, 'F');
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(91, 155, 213);
      doc.text('RESUMEN FINANCIERO', containerMargin + 5, yPosition + 3);
      doc.setTextColor(0, 0, 0);
      yPosition += 10;
      
      autoTable(doc, {
        startY: yPosition,
        head: [['Concepto', 'Monto']],
        body: [
          ['Total de Recibos', totalRecibos.toString()],
          ['Monto Total', `$${totalMonto.toFixed(2)}`],
          ['Total Pagado', `$${totalPagado.toFixed(2)}`],
          ['Saldo Pendiente', `$${totalPendiente.toFixed(2)}`]
        ],
        theme: 'striped',
        headStyles: { 
          fillColor: [91, 155, 213], 
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 10
        },
        styles: { fontSize: 9 },
        columnStyles: {
          0: { cellWidth: 120, halign: 'left' },
          1: { cellWidth: 60, halign: 'right', fontStyle: 'bold' }
        },
        margin: { left: containerMargin, right: containerMargin }
      });
      
      // Tabla de recibos
      const startY = doc.lastAutoTable.finalY + 12;
      
      // Encabezado de detalle
      doc.setFillColor(240, 248, 255); // Azul muy claro
      doc.rect(containerMargin, startY - 2, containerWidth, 8, 'F');
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(91, 155, 213);
      doc.text('DETALLE DE RECIBOS', containerMargin + 5, startY + 3);
      doc.setTextColor(0, 0, 0);
      
      const recibosData = recibos.map(recibo => [
        recibo.numero_recibo || 'N/A',
        new Date(recibo.fecha_emision).toLocaleDateString('es-MX'),
        recibo.fecha_vencimiento ? new Date(recibo.fecha_vencimiento).toLocaleDateString('es-MX') : 'N/A',
        `$${parseFloat(recibo.monto_total || 0).toFixed(2)}`,
        `$${parseFloat(recibo.saldo_pendiente || 0).toFixed(2)}`,
        recibo.estado === 'pagado' ? 'Pagado' : 
        recibo.estado === 'pendiente' ? 'Pendiente' : 
        recibo.estado === 'vencido' ? 'Vencido' : 
        recibo.estado === 'parcial' ? 'Parcial' : recibo.estado
      ]);
      
      autoTable(doc, {
        startY: startY + 10,
        head: [['No. Recibo', 'Emisión', 'Vencimiento', 'Monto', 'Saldo', 'Estado']],
        body: recibosData,
        theme: 'striped',
        headStyles: { 
          fillColor: [91, 155, 213], 
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 9,
          halign: 'center'
        },
        styles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 32, halign: 'left' },
          1: { cellWidth: 26, halign: 'center' },
          2: { cellWidth: 26, halign: 'center' },
          3: { cellWidth: 26, halign: 'right' },
          4: { cellWidth: 26, halign: 'right' },
          5: { cellWidth: 24, halign: 'center' }
        },
        margin: { left: containerMargin, right: containerMargin }
      });
      
      
      // Pie de página
      const pageCount = doc.internal.getNumberOfPages();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(100, 100, 100);
        
        // Texto del pie de página
        doc.text(
          'Estado de cuenta generado automáticamente',
          pageWidth / 2,
          pageHeight - 15,
          { align: 'center' }
        );
        
        // Número de página
        doc.setFont('helvetica', 'normal');
        doc.text(
          `Página ${i} de ${pageCount}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
      }
      
      // Guardar PDF
      const clientNameClean = client?.nombre?.replace(/\s/g, '_') || 'Cliente';
      doc.save(`Estado_Cuenta_${clientNameClean}_${new Date().getTime()}.pdf`);
      
    } catch (error) {
      console.error('Error al generar PDF:', error);
      alert('Error al generar el PDF: ' + error.message);
    }
  };

  const handleSendEmail = () => {
    alert('Enviando recibo por email...');
  };

  const handleScheduleReminders = () => {
    alert('Programando recordatorios...');
  };

  const handleDeleteClient = async () => {
    if (window.confirm('¿Estás seguro de eliminar este cliente? Esta acción no se puede deshacer.')) {
      try {
        await clientesAPI.delete(id);
        alert('Cliente eliminado exitosamente');
        navigate('/clientes');
      } catch (error) {
        alert('Error al eliminar cliente: ' + (error.message || 'Error desconocido'));
      }
    }
  };

  const getEstadoBadge = (estado) => {
    const estadoLower = estado?.toLowerCase();
    return {
      color: estadoLower === 'pagado' ? '#10b981' : 
             estadoLower === 'vencido' ? '#ef4444' : 
             estadoLower === 'parcial' ? '#3b82f6' : '#f59e0b',
      text: estado?.charAt(0).toUpperCase() + estado?.slice(1).toLowerCase()
    };
  };

  if (loading) {
    return (
      <ClientProfileContainer>
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <p>Cargando datos del cliente...</p>
        </div>
      </ClientProfileContainer>
    );
  }

  if (error || !client) {
    return (
      <ClientProfileContainer>
        <div style={{ textAlign: 'center', padding: '60px', color: '#ef4444' }}>
          <p>Error: {error || 'Cliente no encontrado'}</p>
          <Button onClick={() => navigate('/clientes')}>
            Volver a Clientes
          </Button>
        </div>
      </ClientProfileContainer>
    );
  }

  return (
    <ClientProfileContainer>
      <PageHeader>
        <HeaderLeft>
          <BackButton onClick={() => navigate(-1)}>
            <MdArrowBack />
          </BackButton>
          <Title>Ficha de Cliente: {client.nombre}</Title>
        </HeaderLeft>
        
        <ActionButtons>
          <Button onClick={() => navigate('/recibos/nuevo', { state: { clienteId: client.id } })}>
            <MdAttachMoney />
            Nuevo Recibo
          </Button>
          <Button onClick={() => navigate('/registrar-pago', { state: { clienteId: client.id } })}>
            <MdPayment />
            Registrar Pago
          </Button>
          <PrimaryButton onClick={() => navigate(`/clientes/${client.id}/editar`)}>
            <MdEdit />
            Editar Cliente
          </PrimaryButton>
        </ActionButtons>
      </PageHeader>

      <ContentGrid>
        <Card>
          <CardTitle>Información de Contacto</CardTitle>
          
          <ClientInfoSection>
            <Avatar>
              {client.foto_url ? (
                <img src={client.foto_url} alt={client.nombre} />
              ) : (
                <MdPerson style={{ fontSize: '3rem', color: '#9ca3af' }} />
              )}
            </Avatar>
            
            <ClientDetails>
              <ClientName>{client.nombre}</ClientName>
              <ClientEmail>{client.email || 'Sin email'}</ClientEmail>
              
              <ContactInfo>
                {client.telefono && (
                  <ContactItem>
                    <MdPhone />
                    <span>{client.telefono}</span>
                  </ContactItem>
                )}
                {client.direccion && (
                  <ContactItem>
                    <MdLocationOn />
                    <span>{client.direccion}</span>
                  </ContactItem>
                )}
                {client.rfc && (
                  <ContactItem>
                    <MdPerson />
                    <span>RFC: {client.rfc}</span>
                  </ContactItem>
                )}
                {client.regimen_fiscal && (
                  <ContactItem>
                    <MdPerson />
                    <span>Régimen Fiscal: {client.regimen_fiscal}</span>
                  </ContactItem>
                )}
              </ContactInfo>
            </ClientDetails>
          </ClientInfoSection>
        </Card>

        <Card>
          <CardTitle>Resumen Financiero</CardTitle>
          
          <FinancialSummary>
            <SummaryItem>
              <SummaryLabel>Saldo Pendiente</SummaryLabel>
              <SummaryValue style={{ color: parseFloat(client.saldo_pendiente || 0) > 0 ? '#E57373' : '#4CAF50' }}>
                ${parseFloat(client.saldo_pendiente || 0).toFixed(2)}
              </SummaryValue>
            </SummaryItem>

            <SummaryItem>
              <SummaryLabel>Total de Recibos</SummaryLabel>
              <SummaryValue style={{ fontSize: '1.5rem', color: '#3b82f6' }}>
                {recibos.length}
              </SummaryValue>
              <SummarySubtext>
                {recibos.filter(r => r.estado === 'vencido').length} vencidos
              </SummarySubtext>
            </SummaryItem>

            <SummaryItem>
              <SummaryLabel>Fecha de Registro</SummaryLabel>
              <SummaryValue style={{ fontSize: '1.125rem' }}>
                <MdCalendarToday style={{ fontSize: '1rem', verticalAlign: 'middle', marginRight: '4px' }} />
                {new Date(client.fecha_registro).toLocaleDateString('es-MX')}
              </SummaryValue>
            </SummaryItem>
          </FinancialSummary>
        </Card>
      </ContentGrid>

      {/* Sección de Recibos Recurrentes */}
      {recibosRecurrentes && recibosRecurrentes.length > 0 ? (
        <div style={{ marginBottom: '24px' }}>
          {recibosRecurrentes.map((recurrente) => (
            <RecurrentCard key={recurrente.id}>
              <RecurrentHeader>
                <MdRepeat />
                <div>
                  <h3>Cargo Recurrente Activo</h3>
                  <RecurrentBadge>
                    {recurrente.habilitado ? (
                      <>
                        <MdCheckCircle /> Habilitado
                      </>
                    ) : (
                      <>
                        <MdCancel /> Deshabilitado
                      </>
                    )}
                  </RecurrentBadge>
                </div>
              </RecurrentHeader>
              
              <RecurrentInfo>
                <RecurrentInfoItem>
                  <RecurrentLabel>Frecuencia</RecurrentLabel>
                  <RecurrentValue>{recurrente.frecuencia}</RecurrentValue>
                  {recurrente.frecuencia === 'Semanal' && recurrente.dia_semana && (
                    <RecurrentBadge>Cada {recurrente.dia_semana}</RecurrentBadge>
                  )}
                  {recurrente.frecuencia === 'Mensual' && recurrente.dia_mes && (
                    <RecurrentBadge>Día {recurrente.dia_mes} de cada mes</RecurrentBadge>
                  )}
                  {recurrente.frecuencia === 'Anual' && recurrente.mes_anual && recurrente.dia_anual && (
                    <RecurrentBadge>
                      {new Date(2000, recurrente.mes_anual - 1, 1).toLocaleDateString('es-MX', { month: 'long' })} {recurrente.dia_anual}
                    </RecurrentBadge>
                  )}
                </RecurrentInfoItem>
                
                <RecurrentInfoItem>
                  <RecurrentLabel>Monto</RecurrentLabel>
                  <RecurrentValue>${parseFloat(recurrente.monto).toFixed(2)}</RecurrentValue>
                </RecurrentInfoItem>
                
                <RecurrentInfoItem>
                  <RecurrentLabel>Descripción</RecurrentLabel>
                  <RecurrentValue style={{ fontSize: '1rem' }}>{recurrente.descripcion}</RecurrentValue>
                </RecurrentInfoItem>
                
                <RecurrentInfoItem>
                  <RecurrentLabel>Próximo Cobro</RecurrentLabel>
                  <RecurrentValue style={{ fontSize: '1.25rem' }}>
                    <MdCalendarToday style={{ fontSize: '1rem', verticalAlign: 'middle', marginRight: '4px' }} />
                    {new Date(recurrente.proximo_cobro).toLocaleDateString('es-MX')}
                  </RecurrentValue>
                  {recurrente.ultimo_cobro && (
                    <RecurrentLabel style={{ marginTop: '4px' }}>
                      Último: {new Date(recurrente.ultimo_cobro).toLocaleDateString('es-MX')}
                    </RecurrentLabel>
                  )}
                </RecurrentInfoItem>
              </RecurrentInfo>
            </RecurrentCard>
          ))}
        </div>
      ) : (
        <div style={{ marginBottom: '24px' }}>
          <NoRecurrentCard>
            <MdRepeat />
            <p>Este cliente no tiene cargos recurrentes configurados</p>
          </NoRecurrentCard>
        </div>
      )}

      <FullWidthCard>
        <CardTitle>Recibos del Cliente ({recibos.length})</CardTitle>
        
        {recibos.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            <p>Este cliente no tiene recibos registrados</p>
          </div>
        ) : (
          <Table>
            <Thead>
              <tr>
                <Th>Número</Th>
                <Th>Descripción</Th>
                <Th>Fecha Emisión</Th>
                <Th>Vencimiento</Th>
                <Th>Monto Total</Th>
                <Th>Saldo Pendiente</Th>
                <Th>Estado</Th>
                <Th>Acciones</Th>
              </tr>
            </Thead>
            <tbody>
              {recibos.map((recibo) => {
                const estadoBadge = getEstadoBadge(recibo.estado);
                return (
                  <tr key={recibo.id}>
                    <Td>{recibo.numero_recibo}</Td>
                    <Td>{recibo.descripcion || 'Sin descripción'}</Td>
                    <Td>{new Date(recibo.fecha_emision).toLocaleDateString('es-MX')}</Td>
                    <Td>{new Date(recibo.fecha_vencimiento).toLocaleDateString('es-MX')}</Td>
                    <Td>${parseFloat(recibo.monto_total).toFixed(2)}</Td>
                    <Td style={{ 
                      color: parseFloat(recibo.saldo_pendiente) > 0 ? '#ef4444' : '#10b981',
                      fontWeight: '600'
                    }}>
                      ${parseFloat(recibo.saldo_pendiente).toFixed(2)}
                    </Td>
                    <Td>
                      <StatusBadge 
                        status={recibo.estado}
                        style={{ 
                          backgroundColor: estadoBadge.color + '20',
                          color: estadoBadge.color
                        }}
                      >
                        {estadoBadge.text}
                      </StatusBadge>
                    </Td>
                    <Td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <ActionButton onClick={() => navigate(`/recibos/${recibo.id}`)}>
                          <MdVisibility />
                          Ver
                        </ActionButton>
                        {parseFloat(recibo.saldo_pendiente) > 0 && (
                          <ActionButton 
                            onClick={() => navigate('/registrar-pago', { state: { reciboId: recibo.id } })}
                            style={{ backgroundColor: '#10b981', color: 'white' }}
                          >
                            <MdPayment />
                            Pagar
                          </ActionButton>
                        )}
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </FullWidthCard>

      <FullWidthCard>
        <CardTitle>Acciones del Cliente</CardTitle>
        
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>

          <Button 
            onClick={handleDeleteClient}
            style={{ backgroundColor: '#fee2e2', color: '#dc2626', borderColor: '#fecaca' }}
          >
            <MdDelete />
            Eliminar Cliente
          </Button>
          <Button onClick={handleGeneratePDF}>
            Generar Estado de Cuenta PDF
          </Button>
          <Button onClick={handleSendEmail}>
            Enviar Estado de Cuenta por Email
          </Button>
        </div>
      </FullWidthCard>

    </ClientProfileContainer>
  );
};

export default ClientProfile;
